import envirLogoWhite from "@/assets/envir-logo-white.svg";
import brochure from "@/assets/envir-brosjyre.pdf.asset.json";
import priceList from "@/assets/envir-prisliste.pdf.asset.json";
import { Mail, Phone, MapPin, Clock, Facebook, Instagram, Linkedin } from "lucide-react";
import { useLanguage, translations } from "@/i18n/LanguageContext";
import { useLocation } from "react-router-dom";

const Footer = () => {
  const { t } = useLanguage();
  const ft = translations.footer;
  const location = useLocation();
  const isHome = location.pathname === "/";
  // Don't round footer on pages that end with a dark section right above
  const noRoundPages = ["/", "/kontakt"];
  const shouldRound = !noRoundPages.includes(location.pathname);

  return (
    <footer id="kontakt" className={`bg-envir-charcoal section-padding py-16 md:py-24${shouldRound ? " rounded-t-3xl" : ""}`}>
      <div className="max-w-7xl mx-auto">
        <div className="grid md:grid-cols-5 gap-12 md:gap-8 mb-16">
          <div className="md:col-span-1">
            <img src={envirLogoWhite} alt="Envir" className="h-8 w-auto mb-6"  loading="lazy" decoding="async" />
            <p className="text-sm font-body text-primary-foreground/75 leading-relaxed">{t(ft.description)}</p>
          </div>

          <div className="hidden md:block">
            <h3 className="font-display font-600 text-primary-foreground text-sm mb-4">{t(ft.servicesTitle)}</h3>
            <ul className="space-y-3">
              {["/mottak", "/gjenvunne-masser", "/jordvaskeanlegg", "/laboratorium"].map((href, i) => (
                <li key={i}><a href={href} className="text-sm font-body text-primary-foreground/75 hover:text-primary transition-colors">{t(ft.serviceItems[i])}</a></li>
              ))}
            </ul>
          </div>

          <div className="hidden md:block">
            <h3 className="font-display font-600 text-primary-foreground text-sm mb-4">{t(ft.companyTitle)}</h3>
            <ul className="space-y-3">
              {["/om-oss", "/team", "/baerekraft", "/kontakt"].map((href, i) => (
                <li key={i}><a href={href} className="text-sm font-body text-primary-foreground/75 hover:text-primary transition-colors">{t(ft.companyItems[i])}</a></li>
              ))}
            </ul>
          </div>

          <div>
            <h3 className="font-display font-600 text-primary-foreground text-sm mb-4">{t(ft.contactTitle)}</h3>
            <ul className="space-y-3">
              <li className="flex items-center gap-3">
                <Phone className="w-4 h-4 text-primary flex-shrink-0" aria-hidden="true" />
                <a href="tel:+4755200400" className="text-sm font-body text-primary-foreground/75 hover:text-primary transition-colors">+47 55 200 400</a>
              </li>
              <li className="flex items-center gap-3">
                <Mail className="w-4 h-4 text-primary flex-shrink-0" aria-hidden="true" />
                <a href="mailto:post@envir.com" className="text-sm font-body text-primary-foreground/75 hover:text-primary transition-colors">post@envir.com</a>
              </li>
              <li className="flex items-start gap-3">
                <MapPin className="w-4 h-4 text-primary mt-0.5 flex-shrink-0" aria-hidden="true" />
                <span className="text-sm font-body text-primary-foreground/75">Sjøkrigsskoleveien 15<br />5165 Laksevåg</span>
              </li>
            </ul>
          </div>

          <div className="hidden md:block">
            <h3 className="font-display font-600 text-primary-foreground text-sm mb-4">{t(ft.hoursTitle)}</h3>
            <ul className="space-y-3">
              <li className="flex items-start gap-3">
                <Clock className="w-4 h-4 text-primary mt-0.5 flex-shrink-0" aria-hidden="true" />
                <div className="text-sm font-body text-primary-foreground/75">
                  <p>{t(ft.weekdays)}</p>
                  <p>{t(ft.weekends)}</p>
                </div>
              </li>
            </ul>
          </div>
        </div>

        <div className="border-t border-primary-foreground/10 pt-8 flex flex-col md:flex-row justify-between items-center gap-4">
          <p className="text-xs font-body text-primary-foreground/70">© {new Date().getFullYear()} Envir AS. {t(ft.rights)}</p>
          <div className="flex flex-wrap items-center justify-center gap-x-6 gap-y-3">
            <div className="flex items-center gap-4">
              <a href="https://www.facebook.com/envir.as/" target="_blank" rel="noopener noreferrer" aria-label="Envir på Facebook" className="text-primary-foreground/70 hover:text-primary transition-colors"><Facebook className="w-4 h-4" aria-hidden="true" /></a>
              <a href="https://www.instagram.com/envir_as/" target="_blank" rel="noopener noreferrer" aria-label="Envir på Instagram" className="text-primary-foreground/70 hover:text-primary transition-colors"><Instagram className="w-4 h-4" aria-hidden="true" /></a>
              <a href="https://www.linkedin.com/company/envir/" target="_blank" rel="noopener noreferrer" aria-label="Envir på LinkedIn" className="text-primary-foreground/70 hover:text-primary transition-colors"><Linkedin className="w-4 h-4" aria-hidden="true" /></a>
            </div>
            <a href={brochure.url} target="_blank" rel="noopener noreferrer" className="text-xs font-body text-primary-foreground/70 hover:text-primary transition-colors">{t(ft.brochure)}</a>
            <a href={priceList.url} target="_blank" rel="noopener noreferrer" className="text-xs font-body text-primary-foreground/70 hover:text-primary transition-colors">{t(ft.priceList)}</a>
            <a href="/faq" className="text-xs font-body text-primary-foreground/70 hover:text-primary transition-colors">FAQ</a>
            <a href="/personvern" className="text-xs font-body text-primary-foreground/70 hover:text-primary transition-colors">{t(ft.privacy)}</a>
            <a href="/vilkar" className="text-xs font-body text-primary-foreground/70 hover:text-primary transition-colors">{t(ft.terms)}</a>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
